import { useCallback, useEffect, useState, useRef } from "react";
import DatedMappingHistory from "./DatedMappingHistory";
import MappingReadiness from "./MappingReadiness";
import SyntheticMdbLoader from "./SyntheticMdbLoader";
import ConnectorPreview from "./ConnectorPreview";
import PcConnectorSetup from "./PcConnectorSetup";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiChevronRight,
  FiCpu,
  FiEdit2,
  FiLink,
  FiLoader,
  FiMapPin,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiWifi,
  FiX,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import {
  createBiometricDevice,
  getBiometricDevices,
  getBiometricIdentities,
  updateBiometricDevice,
} from "../../services/biometric-devices";

const EMPTY_DEVICE_FORM = {
  code: "",
  name: "",
  branchId: "",
  manufacturer: "eSSL",
  model: "AiFace Neptune ID+B",
  serialNumber: "",
  networkAddress: "",
  networkPort: "4370",
  timeZone: "Asia/Kolkata",
  isActive: true,
};

const asList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

const employeeName = (employee) => {
  if (!employee) return "Employee unavailable";

  const nickname = employee.nickname ? ` (${employee.nickname})` : "";

  return `${employee.fullName || "Unnamed employee"}${nickname}`;
};

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-primary-700 focus:ring-4 focus:ring-primary-100";

export default function BiometricDevices() {
  const { accessToken } = useAuth();

  const [devices, setDevices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [identities, setIdentities] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [deviceModalOpen, setDeviceModalOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [deviceForm, setDeviceForm] = useState(EMPTY_DEVICE_FORM);

  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employees, setEmployees] = useState([]);
  const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
  const [employeeSearchError, setEmployeeSearchError] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [deviceUserId, setDeviceUserId] = useState("");
  const [deviceUserName, setDeviceUserName] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [mappingReason, setMappingReason] = useState("");
  const [identityVerified, setIdentityVerified] = useState(false);
  const [mappingRefresh, setMappingRefresh] = useState(0);
  const mappingRequest = useRef(null);
  const mappingLoadGeneration = useRef(0);
  const selectedDeviceRef = useRef(null);
  useEffect(() => {
    selectedDeviceRef.current = selectedDevice?.id;
    setSelectedEmployee(null);
    setEmployeeSearch("");
    setEmployees([]);
    setDeviceUserId("");
    setDeviceUserName("");
    setValidFrom("");
    setMappingReason("");
    setIdentityVerified(false);
    mappingRequest.current = null;
  }, [selectedDevice?.id]);
  useEffect(() => {
    setIdentityVerified(false);
  }, [selectedEmployee?.id, deviceUserId, validFrom]);

  const clearNotice = () => {
    setError("");
    setSuccess("");
  };

  const loadDevices = useCallback(async () => {
    if (!accessToken) return;

    setLoading(true);
    clearNotice();

    try {
      const [devicesResponse, branchesResponse] = await Promise.all([
        getBiometricDevices(accessToken),
        apiRequest("/biometric-devices/options/branches", { accessToken }),
      ]);

      const nextDevices = asList(devicesResponse);

      setDevices(nextDevices);
      setBranches(asList(branchesResponse));

      setSelectedDevice((currentDevice) => {
        if (!currentDevice) return nextDevices[0] || null;

        return (
          nextDevices.find((device) => device.id === currentDevice.id) ||
          nextDevices[0] ||
          null
        );
      });
      return nextDevices;
    } catch (requestError) {
      setError(requestError.message || "Unable to load face machines.");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  const loadMappings = useCallback(
    async (deviceId) => {
      if (!accessToken || !deviceId) {
        setIdentities([]);
        return;
      }

      setLoadingMappings(true);
      const generation = ++mappingLoadGeneration.current;

      try {
        const response = await getBiometricIdentities(deviceId, accessToken);
        if (
          generation === mappingLoadGeneration.current &&
          selectedDeviceRef.current === deviceId
        )
          setIdentities(asList(response));
      } catch (requestError) {
        if (
          generation === mappingLoadGeneration.current &&
          selectedDeviceRef.current === deviceId
        ) {
          setError(requestError.message || "Unable to load employee mappings.");
          setIdentities([]);
        }
      } finally {
        if (generation === mappingLoadGeneration.current)
          setLoadingMappings(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  useEffect(() => {
    loadMappings(selectedDevice?.id);
  }, [loadMappings, selectedDevice?.id]);

  useEffect(() => {
    let cancelled = false;
    setEmployeeSearchError("");
    if (!accessToken || employeeSearch.trim().length < 2 || selectedEmployee) {
      setEmployees([]);
      setEmployeeSearchLoading(false);
      return;
    }
    setEmployeeSearchLoading(true);
    setEmployees([]);
    const timeout = window.setTimeout(async () => {
      try {
        const response = await apiRequest(
          `/attendance/admin/employees?search=${encodeURIComponent(
            employeeSearch.trim(),
          )}`,
          { accessToken },
        );

        if (!cancelled) setEmployees(asList(response));
      } catch {
        if (!cancelled) {
          setEmployees([]);
          setEmployeeSearchError(
            "Employee search could not load. Try searching again.",
          );
        }
      } finally {
        if (!cancelled) setEmployeeSearchLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [accessToken, employeeSearch, selectedEmployee]);

  const openCreateDevice = () => {
    clearNotice();
    setEditingDevice(null);
    setDeviceForm(EMPTY_DEVICE_FORM);
    setDeviceModalOpen(true);
  };

  const openEditDevice = (device = selectedDevice) => {
    if (!device) return;

    clearNotice();
    setEditingDevice(device);

    setDeviceForm({
      code: device.code || "",
      name: device.name || "",
      branchId: device.branchId || "",
      manufacturer: device.manufacturer || "eSSL",
      model: device.model || "",
      serialNumber: device.serialNumber || "",
      networkAddress: device.networkAddress || "",
      networkPort: String(device.networkPort || 4370),
      timeZone: device.timeZone || "Asia/Kolkata",
      isActive: device.isActive !== false,
    });

    setDeviceModalOpen(true);
  };

  const saveDevice = async (event) => {
    event.preventDefault();

    setSaving(true);
    clearNotice();

    try {
      const payload = {
        ...deviceForm,
        code: deviceForm.code.trim().toUpperCase(),
        name: deviceForm.name.trim(),
        branchId: deviceForm.branchId,
        manufacturer: deviceForm.manufacturer.trim() || undefined,
        model: deviceForm.model.trim() || undefined,
        serialNumber: deviceForm.serialNumber.trim() || undefined,
        networkAddress: deviceForm.networkAddress.trim() || undefined,
        networkPort: deviceForm.networkPort
          ? Number(deviceForm.networkPort)
          : undefined,
        timeZone: deviceForm.timeZone.trim() || "Asia/Kolkata",
      };

      if (editingDevice) {
        await updateBiometricDevice(editingDevice.id, payload, accessToken);
        setSuccess("Face machine updated successfully.");
      } else {
        await createBiometricDevice(payload, accessToken);
        setSuccess(
          "Face machine added. No data on the physical machine was changed.",
        );
      }

      setDeviceModalOpen(false);
      await loadDevices();
    } catch (requestError) {
      setError(requestError.message || "Unable to save the face machine.");
    } finally {
      setSaving(false);
    }
  };

  const addMapping = async (event) => {
    event.preventDefault();

    if (
      !selectedDevice ||
      !selectedEmployee ||
      !deviceUserId.trim() ||
      !validFrom ||
      !identityVerified ||
      mappingReason.trim().length < 10
    ) {
      setError(
        "Choose an employee and enter their existing User ID from the machine.",
      );
      return;
    }

    setSaving(true);
    clearNotice();

    try {
      const mappingBody = {
        employeeId: selectedEmployee.id,
        deviceUserId: deviceUserId.trim(),
        deviceUserName: deviceUserName.trim() || undefined,
        validFrom,
        reason: mappingReason.trim(),
        identityVerified: true,
      };
      const signature = JSON.stringify({
        deviceId: selectedDevice.id,
        ...mappingBody,
      });
      const requestDeviceId = selectedDevice.id;
      if (mappingRequest.current?.signature !== signature)
        mappingRequest.current = {
          signature,
          requestKey: window.crypto.randomUUID(),
        };
      await apiRequest(
        `/biometric-devices/${selectedDevice.id}/dated-mappings`,
        {
          method: "POST",
          accessToken,
          body: {
            ...mappingBody,
            requestKey: mappingRequest.current.requestKey,
          },
        },
      );
      if (selectedDeviceRef.current !== requestDeviceId) return;
      mappingRequest.current = null;
      setMappingRefresh((x) => x + 1);
      setValidFrom("");
      setMappingReason("");
      setIdentityVerified(false);

      setDeviceUserId("");
      setDeviceUserName("");
      setEmployeeSearch("");
      setSelectedEmployee(null);

      setSuccess(
        "Employee linked successfully. No face template or attendance data was changed.",
      );

      await loadMappings(selectedDevice.id);
      await loadDevices();
    } catch (requestError) {
      setError(requestError.message || "Unable to link this employee.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[65vh] items-center justify-center">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white px-5 py-4 shadow-sm">
          <FiLoader className="animate-spin text-primary-800" size={22} />
          <span className="font-medium text-slate-600">
            Loading face machines…
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto space-y-6">
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Face Machines</h1>
          <p className="mt-2 text-gray-500">
            Manage attendance devices and link their existing User IDs to
            employees.
          </p>
        </div>

        <button
          type="button"
          onClick={openCreateDevice}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-800 px-5 py-3 font-medium text-white shadow-sm transition-all hover:bg-red-900"
        >
          <FiPlus size={18} />
          Add Face Machine
        </button>
      </div>

      <SyntheticMdbLoader accessToken={accessToken} onLoaded={async result=>{const next=await loadDevices();setSelectedDevice(next?.find(d=>d.id===result.deviceId)||null);}} />

      {error ? (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <FiAlertCircle className="mt-0.5 shrink-0" size={18} />
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <FiCheckCircle className="mt-0.5 shrink-0" size={18} />
          {success}
        </div>
      ) : null}

      <div className="mb-6 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm text-gray-500">
              Showing{" "}
              <span className="font-semibold text-gray-900">
                {devices.length}
              </span>{" "}
              registered face machine{devices.length === 1 ? "" : "s"}
            </p>

            <p className="mt-1 text-xs text-gray-400">
              Active:{" "}
              {devices.filter((device) => device.isActive !== false).length}
              {" · "}
              Select a device below to manage employee mappings.
            </p>
          </div>

          <button
            type="button"
            onClick={loadDevices}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
          >
            <FiRefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left">
            <thead className="border-b border-gray-100 bg-gray-50">
              <tr className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-5 py-4">Face Machine</th>
                <th className="px-5 py-4">Branch</th>
                <th className="px-5 py-4">Connection</th>
                <th className="px-5 py-4">Legacy undated links</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4 text-right">Action</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {devices.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-5 py-16 text-center">
                    <FiCpu className="mx-auto text-gray-300" size={36} />
                    <p className="mt-3 font-semibold text-gray-700">
                      No face machines found
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      Add your eSSL device safely. This does not modify data on
                      the physical machine.
                    </p>
                  </td>
                </tr>
              ) : (
                devices.map((device) => {
                  const selected = selectedDevice?.id === device.id;

                  return (
                    <tr
                      key={device.id}
                      onClick={() => {
                        clearNotice();
                        setSelectedDevice(device);
                      }}
                      className={`cursor-pointer transition ${
                        selected ? "bg-red-50/60" : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className={`rounded-xl p-2.5 ${
                              selected
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            <FiCpu size={19} />
                          </div>

                          <div>
                            <p className="font-semibold text-gray-900">
                              {device.name}
                            </p>
                            <p className="mt-1 text-xs font-medium text-red-800">
                              {device.code}
                            </p>
                            <p className="mt-1 text-xs text-gray-500">
                              {device.manufacturer || "eSSL"} ·{" "}
                              {device.model || "Model not recorded"}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <FiMapPin className="text-gray-400" />
                          {device.branch?.name || "Not assigned"}
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <p className="flex items-center gap-2 text-sm text-gray-700">
                          <FiWifi className="text-gray-400" />
                          {device.networkAddress || "Not configured"}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          Port {device.networkPort || 4370}
                        </p>
                      </td>

                      <td className="px-5 py-4 text-sm font-semibold text-gray-700">
                        {device._count?.identities || 0}
                      </td>

                      <td className="px-5 py-4">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                            device.isActive !== false
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {device.isActive !== false ? "Active" : "Inactive"}
                        </span>
                      </td>

                      <td className="px-5 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              openEditDevice(device);
                            }}
                            className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
                            title="Edit machine"
                          >
                            <FiEdit2 size={17} />
                          </button>

                          <button
                            type="button"
                            onClick={() => setSelectedDevice(device)}
                            className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-red-800 transition hover:bg-red-100"
                          >
                            Manage
                            <FiChevronRight size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedDevice ? (
        <section className="mt-8">
          <PcConnectorSetup key={`setup-${selectedDevice.id}`} device={selectedDevice} accessToken={accessToken}/>
          <ConnectorPreview key={selectedDevice.id} device={selectedDevice} accessToken={accessToken} refresh={mappingRefresh}/>
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-sm font-medium text-red-800">
                Selected Face Machine
              </p>
              <h2 className="mt-1 text-2xl font-bold text-gray-900">
                {selectedDevice.name}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Manage existing User IDs enrolled on this device.
              </p>
            </div>

            <button
              type="button"
              onClick={openEditDevice}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
            >
              <FiEdit2 size={16} />
              Edit Device
            </button>
          </div>

          <div className="flex flex-col gap-6">
            <details className="rounded-2xl border border-gray-200 bg-white shadow-sm">
            <summary className="cursor-pointer px-5 py-4 text-sm font-semibold text-red-800 hover:bg-red-50">Link an employee <span className="ml-2 font-normal text-slate-500">Add a reviewed machine ID assignment</span></summary>
            <form
              onSubmit={addMapping}
              className="h-fit rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
            >
              <fieldset disabled={saving}>
                <div className="mb-5">
                  <div className="flex items-center gap-2">
                    <FiLink className="text-red-800" />
                    <h3 className="font-bold text-gray-900">
                      Link an employee
                    </h3>
                  </div>

                  <p className="mt-2 text-sm leading-6 text-gray-500">
                    Connect an existing machine user to their HRMS employee
                    record. All required fields are marked *. Saving does not
                    change attendance or payroll.
                  </p>
                </div>

                <label className="block text-sm font-medium text-gray-700">
                  Machine User ID *
                  <input
                    required
                    value={deviceUserId}
                    onChange={(event) => setDeviceUserId(event.target.value)}
                    placeholder="Enter the ID shown in eSSL"
                    maxLength={50}
                    aria-describedby="machine-id-help"
                    className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-3 outline-none focus:border-red-800 focus:ring-2 focus:ring-red-100"
                  />
                </label>
                <p
                  id="machine-id-help"
                  className="mt-2 text-xs leading-5 text-gray-500"
                >
                  Copy the exact ID, including leading zeros. It may differ from
                  the HRMS employee code.
                </p>

                <label className="mt-4 block text-sm font-medium text-gray-700">
                  Search Employee *
                  <div className="relative mt-2">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      value={employeeSearch}
                      onChange={(event) => {
                        setEmployeeSearch(event.target.value);
                        setSelectedEmployee(null);
                      }}
                      placeholder="Name, nickname, or employee code"
                      className="w-full rounded-xl border border-gray-200 py-3 pl-10 pr-3 outline-none focus:border-red-800 focus:ring-2 focus:ring-red-100"
                    />
                  </div>
                </label>

                {!selectedEmployee && employeeSearch.trim().length >= 2 ? (
                  <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-gray-200 bg-white">
                    {employeeSearchLoading ? (
                      <p className="p-4 text-sm text-gray-500" role="status">
                        Searching employees…
                      </p>
                    ) : employeeSearchError ? (
                      <p className="p-4 text-sm text-red-700" role="alert">
                        {employeeSearchError}
                      </p>
                    ) : employees.length ? (
                      employees.map((employee) => (
                        <button
                          key={employee.id}
                          type="button"
                          onClick={() => {
                            setSelectedEmployee(employee);
                            setEmployeeSearch(employeeName(employee));
                            setEmployees([]);
                          }}
                          className="block w-full border-b border-gray-100 px-4 py-3 text-left last:border-b-0 hover:bg-red-50"
                        >
                          <p className="font-semibold text-gray-900">
                            {employeeName(employee)}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            {employee.employeeCode} ·{" "}
                            {employee.branch?.name || "No branch"}
                          </p>
                        </button>
                      ))
                    ) : (
                      <p className="p-4 text-sm text-gray-500">
                        No employee found.
                      </p>
                    )}
                  </div>
                ) : null}

                {selectedEmployee ? (
                  <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-green-200 bg-green-50 p-3">
                    <div>
                      <p className="text-sm font-semibold text-green-800">
                        {employeeName(selectedEmployee)}
                      </p>
                      <p className="mt-1 text-xs text-green-700">
                        {selectedEmployee.employeeCode}
                      </p>
                    </div>

                    <button
                      type="button"
                      aria-label="Clear selected employee"
                      onClick={() => {
                        setSelectedEmployee(null);
                        setEmployeeSearch("");
                      }}
                      className="rounded-lg p-1.5 text-green-700 hover:bg-green-100"
                    >
                      <FiX />
                    </button>
                  </div>
                ) : null}

                <label className="mt-4 block text-sm font-medium text-gray-700">
                  Name shown on machine
                  <span className="ml-1 font-normal text-gray-400">
                    (optional)
                  </span>
                  <input
                    value={deviceUserName}
                    onChange={(event) => setDeviceUserName(event.target.value)}
                    placeholder="Optional display name from eSSL"
                    maxLength={150}
                    className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-3 outline-none focus:border-red-800 focus:ring-2 focus:ring-red-100"
                  />
                </label>

                <label className="mt-4 block">
                  First valid date (inclusive) *
                  <input
                    type="date"
                    required
                    min={selectedEmployee?.joiningDate?.slice(0, 10)}
                    value={validFrom}
                    onChange={(e) => setValidFrom(e.target.value)}
                    className={inputClass}
                  />
                </label>
                <p className="mt-2 text-xs leading-5 text-gray-500">
                  This employee owns the machine ID from this date. Earlier
                  punches will not use this assignment.
                </p>
                <label className="mt-4 block">
                  Mapping review reason *
                  <textarea
                    required
                    rows={3}
                    placeholder="Explain how you verified the employee and machine ID"
                    minLength={10}
                    maxLength={1000}
                    value={mappingReason}
                    onChange={(e) => setMappingReason(e.target.value)}
                    className={inputClass}
                  />
                </label>
                <p className="mt-1 text-right text-xs text-gray-500">
                  {mappingReason.trim().length} / 10 minimum characters
                </p>
                <label className="mt-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 shrink-0 accent-red-800"
                    checked={identityVerified}
                    onChange={(e) => setIdentityVerified(e.target.checked)}
                  />
                  <span>
                    I checked this employee’s exact machine ID and first valid
                    date. No automatic name or employee-code matching is
                    assumed.
                  </span>
                </label>
                <MappingReadiness
                  employee={selectedEmployee}
                  machineId={deviceUserId}
                  startDate={validFrom}
                  reason={mappingReason}
                  verified={identityVerified}
                  deviceActive={selectedDevice.isActive !== false}
                />
                <button
                  type="submit"
                  aria-describedby="mapping-readiness"
                  disabled={
                    saving ||
                    selectedDevice.isActive === false ||
                    !selectedEmployee ||
                    !deviceUserId.trim() ||
                    !validFrom ||
                    !identityVerified ||
                    mappingReason.trim().length < 10
                  }
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-red-800 px-4 py-3 font-medium text-white transition hover:bg-red-900 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? <FiLoader className="animate-spin" /> : <FiLink />}
                  {saving ? "Linking employee…" : "Link Employee"}
                </button>
              </fieldset>
            </form>
            </details>

            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
              <DatedMappingHistory
                key={selectedDevice.id}
                deviceId={selectedDevice.id}
                accessToken={accessToken}
                refresh={mappingRefresh}
              />
              <details className="border-t border-slate-100">
                <summary className="cursor-pointer px-5 py-4 text-sm font-semibold text-slate-500 hover:bg-slate-50">
                  Legacy undated links{" "}
                  <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs">
                    {identities.length}
                  </span>
                  <span className="ml-2 text-xs font-normal">
                    Reference only
                  </span>
                </summary>
                <div className="flex items-center justify-between border-b border-gray-100 p-5">
                  <div>
                    <h3 className="font-bold text-gray-900">
                      Legacy undated mappings (reference only)
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      These links are not used by the dated connector preview.
                      HR must verify dates and create dated assignments; no
                      history is inferred.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => loadMappings(selectedDevice.id)}
                    aria-label="Refresh legacy links"
                    className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 hover:text-red-800"
                  >
                    <FiRefreshCw
                      className={loadingMappings ? "animate-spin" : ""}
                    />
                  </button>
                </div>

                {loadingMappings ? (
                  <div className="py-16 text-center text-gray-500">
                    <FiLoader
                      className="mx-auto animate-spin text-red-800"
                      size={25}
                    />
                    <p className="mt-3">Loading employee mappings…</p>
                  </div>
                ) : identities.length === 0 ? (
                  <div className="px-5 py-5 text-center">
                    <p className="mt-3 font-semibold text-gray-700">
                      No legacy undated links
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      New dated assignments appear in the history above.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left">
                      <thead className="border-b border-gray-100 bg-gray-50">
                        <tr className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          <th className="px-5 py-4">Machine User ID</th>
                          <th className="px-5 py-4">Employee</th>
                          <th className="px-5 py-4">Machine Name</th>
                          <th className="px-5 py-4">Status</th>
                          <th className="px-5 py-4 text-right">Action</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-100">
                        {identities.map((identity) => (
                          <tr key={identity.id}>
                            <td className="px-5 py-4 font-semibold text-red-800">
                              {identity.deviceUserId}
                            </td>

                            <td className="px-5 py-4">
                              <p className="font-semibold text-gray-900">
                                {employeeName(identity.employee)}
                              </p>
                              <p className="mt-1 text-xs text-gray-500">
                                {identity.employee?.employeeCode || "—"}
                              </p>
                            </td>

                            <td className="px-5 py-4 text-sm text-gray-600">
                              {identity.deviceUserName || "—"}
                            </td>

                            <td className="px-5 py-4">
                              <span
                                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                                  identity.isActive !== false
                                    ? "bg-green-100 text-green-700"
                                    : "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {identity.isActive !== false
                                  ? "Active"
                                  : "Inactive"}
                              </span>
                            </td>

                            <td className="px-5 py-4 text-right">
                              Undated reference
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </details>
            </div>
          </div>
        </section>
      ) : null}

      {deviceModalOpen ? (
        <div className="fixed inset-0 z-[100] overflow-y-auto bg-slate-950/60 p-4 sm:p-8">
          <div className="flex min-h-full items-center justify-center">
            <form
              onSubmit={saveDevice}
              className="w-full max-w-3xl overflow-hidden rounded-3xl bg-white shadow-2xl"
            >
              <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-5 sm:p-6">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-primary-700">
                    Device Configuration
                  </p>
                  <h2 className="mt-1 text-xl font-bold text-slate-900">
                    {editingDevice ? "Edit Face Machine" : "Add Face Machine"}
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    This only stores CHARMS configuration. It does not connect
                    to or change the physical machine.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setDeviceModalOpen(false)}
                  className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100"
                >
                  <FiX size={21} />
                </button>
              </div>

              <div className="grid gap-5 p-5 sm:grid-cols-2 sm:p-6">
                <Field label="Device code *">
                  <input
                    required
                    value={deviceForm.code}
                    onChange={(event) =>
                      setDeviceForm((current) => ({
                        ...current,
                        code: event.target.value,
                      }))
                    }
                    placeholder="MAIN_FACE_01"
                    className={inputClass}
                  />
                </Field>

                <Field label="Display name *">
                  <input
                    required
                    value={deviceForm.name}
                    onChange={(event) =>
                      setDeviceForm((current) => ({
                        ...current,
                        name: event.target.value,
                      }))
                    }
                    placeholder="Main Office Face Machine"
                    className={inputClass}
                  />
                </Field>

                <Field label="Branch *">
                  <select
                    required
                    value={deviceForm.branchId}
                    onChange={(event) =>
                      setDeviceForm((current) => ({
                        ...current,
                        branchId: event.target.value,
                      }))
                    }
                    className={inputClass}
                  >
                    <option value="">Select branch</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name} ({branch.code})
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Manufacturer">
                  <input
                    value={deviceForm.manufacturer}
                    onChange={(event) =>
                      setDeviceForm((current) => ({
                        ...current,
                        manufacturer: event.target.value,
                      }))
                    }
                    className={inputClass}
                  />
                </Field>

                <Field label="Model">
                  <input
                    value={deviceForm.model}
                    onChange={(event) =>
                      setDeviceForm((current) => ({
                        ...current,
                        model: event.target.value,
                      }))
                    }
                    className={inputClass}
                  />
                </Field>

                <Field label="Serial number">
                  <input
                    value={deviceForm.serialNumber}
                    onChange={(event) =>
                      setDeviceForm((current) => ({
                        ...current,
                        serialNumber: event.target.value,
                      }))
                    }
                    placeholder="HDP1252400116"
                    className={inputClass}
                  />
                </Field>

                <Field label="Local IP address">
                  <input
                    value={deviceForm.networkAddress}
                    onChange={(event) =>
                      setDeviceForm((current) => ({
                        ...current,
                        networkAddress: event.target.value,
                      }))
                    }
                    placeholder="192.168.1.201"
                    className={inputClass}
                  />
                </Field>

                <Field label="Port">
                  <input
                    type="number"
                    min="1"
                    max="65535"
                    value={deviceForm.networkPort}
                    onChange={(event) =>
                      setDeviceForm((current) => ({
                        ...current,
                        networkPort: event.target.value,
                      }))
                    }
                    className={inputClass}
                  />
                </Field>

                <Field label="Time zone">
                  <input
                    value={deviceForm.timeZone}
                    onChange={(event) =>
                      setDeviceForm((current) => ({
                        ...current,
                        timeZone: event.target.value,
                      }))
                    }
                    className={inputClass}
                  />
                </Field>

                <label className="flex items-center gap-3 self-end rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-700">
                  <input
                    type="checkbox"
                    checked={deviceForm.isActive}
                    onChange={(event) =>
                      setDeviceForm((current) => ({
                        ...current,
                        isActive: event.target.checked,
                      }))
                    }
                    className="h-4 w-4 accent-primary-800"
                  />
                  Machine is active
                </label>
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-slate-100 p-5 sm:flex-row sm:justify-end sm:p-6">
                <button
                  type="button"
                  onClick={() => setDeviceModalOpen(false)}
                  className="rounded-xl border border-slate-200 px-5 py-3 font-bold text-slate-700 transition hover:bg-slate-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-800 px-5 py-3 font-bold text-white transition hover:bg-primary-900 disabled:opacity-60"
                >
                  {saving ? (
                    <FiLoader className="animate-spin" />
                  ) : (
                    <FiChevronRight />
                  )}
                  {editingDevice ? "Save Changes" : "Add Face Machine"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
